import { apiPost } from '@/api/main/client'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { withdrawsAPITrader } from '../api'
import { convertFileToBase64 } from '@/shared/utils/convertFileToBase64'
import { useToast } from '@/shared/hooks'
import { ApiError } from '@/api/types'

interface AttachReceiptParams {
  withdrawId: string
  file: File
}

interface ReceiptFileAttachment {
  originalFileName: string
  base64FileData: string
}

interface AttachReceiptRequest {
  withdrawId: string
  receiptFileAttachment: ReceiptFileAttachment
}

interface DeleteReceiptParams {
  withdrawId: string
  fileId: string
}

interface DeleteReceiptRequest {
  withdrawId: string
  fileId: string
}

export const useReceiptManagement = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const attachReceiptMutation = useMutation({
    mutationFn: async ({ withdrawId, file }: AttachReceiptParams) => {
      const fileData = await convertFileToBase64(file)

      const requestData: AttachReceiptRequest = {
        withdrawId,
        receiptFileAttachment: {
          originalFileName: file.name,
          base64FileData: fileData.base64,
        },
      }

      return apiPost(withdrawsAPITrader.attachReceipt, requestData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdraws', 'paid'] })
      addToast('Успех!', 'Чек успешно прикреплен', 'success')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.failures?.map((f) => f.description).join(', ') ||
        'Не удалось прикрепить чек'
      addToast('Ошибка!', errorMessage, 'error')
    },
  })

  const deleteReceiptMutation = useMutation({
    mutationFn: async ({ withdrawId, fileId }: DeleteReceiptParams) => {
      const requestData: DeleteReceiptRequest = {
        withdrawId,
        fileId,
      }

      return apiPost(withdrawsAPITrader.deleteReceipt, requestData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdraws', 'paid'] })
      addToast('Успех!', 'Чек успешно удален', 'success')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.failures?.map((f) => f.description).join(', ') ||
        'Не удалось удалить чек'
      addToast('Ошибка!', errorMessage, 'error')
    },
  })

  return {
    attachReceipt: attachReceiptMutation.mutate,
    isAttaching: attachReceiptMutation.isPending,
    deleteReceipt: deleteReceiptMutation.mutate,
    isDeleting: deleteReceiptMutation.isPending,
  }
}
